/**
 * Halka açık EXPO soru formu — giriş GEREKTİRMEZ.
 *
 * Proje sorumlusuna e-posta ile gönderilen linkten açılır
 * (/expo-form/:token). Yanıtlar gönderilince backend projede
 * "Proje To Do List" listesini otomatik oluşturur.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import QuestionWizard, {
  buildAnswersPayload,
} from "../components/expo/QuestionWizard";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicExpoForm() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/api/public/expo-form/${token}`)
      .then(({ data }) => setData(data))
      .catch((err) =>
        setError(err.response?.data?.detail || "Form yüklenemedi"),
      );
  }, [token]);

  const submit = async () => {
    const payload = buildAnswersPayload(data.questions, answers);
    if (payload.length === 0) {
      setError("Lütfen en az bir soruyu yanıtlayın.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/expo-form/${token}`, {
        answers: payload,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Gönderilemedi, tekrar deneyin.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 px-4 py-10">
      <div className="mx-auto max-w-xl">
        <div className="mb-6 text-center">
          <span className="font-display text-2xl font-bold tracking-tight text-zinc-900">
            Serpix
          </span>
        </div>
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm" data-testid="public-expo-form">
          {!data && !error && (
            <div className="flex h-32 items-center justify-center text-zinc-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          )}

          {error && !data && (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
              <div className="text-sm text-zinc-600">{error}</div>
            </div>
          )}

          {data && done && (
            <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="public-expo-form-done">
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
              <div className="text-lg font-semibold text-zinc-900">
                Teşekkürler!
              </div>
              <p className="text-sm text-zinc-500">
                Yanıtlarınız alındı — <b>{data.project_name}</b> projesinin to
                do listesi otomatik olarak oluşturuldu.
              </p>
            </div>
          )}

          {data && !done && data.status !== "pending" && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
              <div className="text-sm text-zinc-600">
                Bu form daha önce yanıtlanmış. Teşekkür ederiz.
              </div>
            </div>
          )}

          {data && !done && data.status === "pending" && (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
                {data.project_name} — Proje To Do List Soruları
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                Aşağıdaki soruları yanıtlayın; yanıtlarınız alındığında proje
                to do listesi otomatik oluşturulur.
              </p>
              <div className="mt-5">
                <QuestionWizard
                  questions={data.questions}
                  answers={answers}
                  onChange={setAnswers}
                />
              </div>
              {error && (
                <div className="mt-3 text-sm text-red-600">{error}</div>
              )}
              <Button
                onClick={submit}
                disabled={busy}
                className="mt-5 w-full gap-2 bg-zinc-900 text-white hover:bg-zinc-800"
                data-testid="public-expo-form-submit"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Yanıtları gönder
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
